import { Navigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useAuth, userHasRole, userHoldsExactRole } from './AuthContext';
import { isProfileComplete } from './profileComplete';
import type { CurrentUser, Role } from '../api/users';

/**
 * ROADMAP #18 phase 2: enrollment is now governed by an org/platform
 * policy rather than a hardcoded role list. The backend's /users/me
 * computes a `totp_required` boolean:
 *   - True when the user holds an active superadmin role AND the
 *     platform `mfa_policy.totp_required` is True, OR
 *   - True when any active tenant_admin membership lives in a tenant
 *     with `mfa_required` = True.
 * Participants stay opt-in (their own toggle on Profile).
 *
 * When `totp_required` is True and `totp_enabled_at` is null, the
 * gate routes here to /setup-2fa.
 */
function totpRequiredButMissing(user: CurrentUser): boolean {
  return user.totp_required && user.totp_enabled_at === null;
}

/**
 * Route guard. Behaviours, in order:
 *   - While the initial /refresh roundtrip is in flight, show a
 *     centered spinner — prevents a flash of /login on hard reloads
 *     when the user actually does have a valid refresh cookie.
 *   - Not authenticated → redirect to /login, capturing `from` so
 *     the login page can send the user back here afterwards.
 *   - Authenticated but missing the required role → redirect to a
 *     generic landing page (/profile, which every authed user can
 *     reach).
 *   - Authenticated, but holds a TOTP-required role with no TOTP
 *     enabled → redirect to /setup-2fa. Skips when already there.
 *   - Authenticated, role matches, but the user's profile is
 *     incomplete (mandatory fields blank — full_name for any role,
 *     plus title + years_of_experience for participants) → redirect
 *     to /profile so the welcome-banner flow can take over. Skips
 *     this redirect when the user is already on /profile to avoid a
 *     loop.
 *
 * The gates fire on every authed navigation, not just landings, so a
 * user who bailed halfway through any onboarding step still gets
 * bounced back next session.
 */
export function RequireAuth({
  children,
  role,
}: {
  children: React.ReactNode;
  /**
   * Required role. Omit for "any authenticated user". Pass an array to
   * admit any of several roles — e.g. `['tenant_admin', 'coordinator']`
   * for operational pages both staff roles can reach (TENANT-VIEWER-ROLE).
   * Superadmin wildcards onto every role via userHasRole.
   */
  role?: Role | Role[];
}) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Never-granted user (verified self-registration, zero memberships)
  // → the "awaiting access" screen. They can't use the app shell until
  // a superadmin grants a role. Checked before the role/totp/profile
  // gates since none apply without a role. Suspended-only users (a row
  // exists but is inactive) keep the normal role-gate path.
  if (user.memberships.length === 0 && location.pathname !== '/pending') {
    return <Navigate to="/pending" replace />;
  }

  const roles = role === undefined ? [] : Array.isArray(role) ? role : [role];
  if (roles.length > 0 && !roles.some((r) => userHasRole(user, r))) {
    return <Navigate to="/profile" replace />;
  }
  // A platform admin has no standing access to a tenant's data
  // (SUPPORT-IMPERSONATION): the API refuses every tenant route unless they
  // open a support session, which makes them the tenant admin they act as.
  // userHasRole still wildcards superadmin onto every role, so tenant-only
  // pages opened for a bare platform admin and then failed on every request
  // (prod, 2026-10-08). Send them to the Tenants page instead, where the
  // support session starts.
  if (
    roles.length > 0 &&
    !roles.includes('superadmin') &&
    !roles.some((r) => userHoldsExactRole(user, r)) &&
    userHoldsExactRole(user, 'superadmin')
  ) {
    return (
      <Navigate to="/admin/tenants" replace state={{ notice: 'tenant-needs-support-session' }} />
    );
  }

  // TOTP enrollment gate fires BEFORE the profile-completion gate so
  // that an unfilled profile + missing TOTP doesn't ping-pong between
  // /profile and /setup-2fa — TOTP first, then profile.
  if (totpRequiredButMissing(user) && location.pathname !== '/setup-2fa') {
    return <Navigate to="/setup-2fa" replace />;
  }

  // Profile-completion gate. Deferred until TOTP is satisfied: a user
  // who owes BOTH (e.g. a freshly-bootstrapped superadmin with no
  // full_name and no TOTP yet) would otherwise ping-pong between
  // /setup-2fa (profile gate sends → /profile) and /profile (TOTP gate
  // sends → /setup-2fa), an infinite redirect loop that blanks the app.
  // While TOTP is owed, only the TOTP gate above acts.
  if (
    !totpRequiredButMissing(user) &&
    !isProfileComplete(user) &&
    location.pathname !== '/profile'
  ) {
    return <Navigate to="/profile" replace />;
  }

  return <>{children}</>;
}
